import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import BarcodeReader from "react-barcode-reader";
import BootstrapTable from "react-bootstrap-table-next";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  PaginationTotalStandalone,
} from "react-bootstrap-table2-paginator";
import axiosInstance from "Axiosinstance";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import { Col, Row } from "reactstrap";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import Loader from "components/Loader";
import { v4 as uuid } from "uuid";
import { local } from "utils/Constant";

const MAX_TAGS = 50;
const LOGS_PAGE_SIZE = 10;

const HISTORY_TABS = {
  PRODUCT_CATEGORY: 0,
  POLICY: 1,
};

const VALIDITY_UNITS = ["Days", "Months", "Year"];

const toDateInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const hasValidityNumberAndUnit = (number, unit) => {
  if (!unit) return false;
  if (number === null || number === undefined || number === "") return false;
  const amount = Number(number);
  return !Number.isNaN(amount) && amount >= 0;
};

const computeExpiryDate = (number, unit) => {
  if (!hasValidityNumberAndUnit(number, unit)) return "";

  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const amount = Number(number);
  const expiry = new Date(start);
  if (unit === "Days") {
    expiry.setDate(expiry.getDate() + amount);
  } else if (unit === "Months") {
    expiry.setMonth(expiry.getMonth() + amount);
  } else if (unit === "Year" || unit === "Years") {
    expiry.setFullYear(expiry.getFullYear() + amount);
  } else {
    return "";
  }
  return toDateInputValue(expiry);
};

const EMPTY_TAG_FORM = {
  tag_number: "",
  product_category: "",
  company_name: "",
  validity_from_number: "",
  validity_from_unit: "",
  validity_start: "",
  expiry_date: "",
  isDeleted: false,
};

const getAdminStatusMessageColor = (adminApproved) => {
  if (adminApproved === "accepted") return "green";
  if (adminApproved === "rejected") return "red";
  return "#ed6c02";
};

const getPolicyStatusColor = (status) => (status === true ? "green" : "red");

const formatPolicyStatus = (status) => (status === true ? "success" : "failed");

const renderTableCell = (cell) => (
  <h5 className="font-size-14 mb-1">{cell ?? ""}</h5>
);

const renderColoredCell = (cell, color) => (
  <h5 className="font-size-14 mb-1">
    <span style={{ color, fontWeight: 600 }}>{cell ?? ""}</span>
  </h5>
);

const mapLogRows = (logs = [], page = 1) =>
  logs.map((log, index) => ({
    ...log,
    id: log._id?.toString?.() || log._id || log.id || `log-${page}-${index}`,
  }));

function TagUpdate() {
  const [loader, setLoader] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [policyLogsLoading, setPolicyLogsLoading] = useState(false);
  const [policyLogs, setPolicyLogs] = useState([]);
  const [policyLogsPage, setPolicyLogsPage] = useState(1);
  const [policyLogsTotal, setPolicyLogsTotal] = useState(0);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateType, setUpdateType] = useState("product_category");
  const [allCategories, setAllCategories] = useState([]);
  const [productCategory, setProductCategory] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [totalScan, setTotalScan] = useState(0);
  const [qrError, setQrError] = useState(false);
  const [scanValidationError, setScanValidationError] = useState("");
  const [pendingTags, setPendingTags] = useState([]);
  const [sampleTagInputOpen, setSampleTagInputOpen] = useState(false);
  const [sampleTagValue, setSampleTagValue] = useState("");
  const [insuranceValue, setInsuranceValue] = useState("");
  const [historyTab, setHistoryTab] = useState(HISTORY_TABS.PRODUCT_CATEGORY);
  const [logsSearchInput, setLogsSearchInput] = useState("");
  const [logsSearch, setLogsSearch] = useState("");
  const [logsStatusFilter, setLogsStatusFilter] = useState("");
  const [policyLogsSearchInput, setPolicyLogsSearchInput] = useState("");
  const [policyLogsSearch, setPolicyLogsSearch] = useState("");
  const [policyLogsStatusFilter, setPolicyLogsStatusFilter] = useState("");
  const [tagValidationResult, setTagValidationResult] = useState(null);
  const [tagValidationOpen, setTagValidationOpen] = useState(false);
  const [tagValidationLoading, setTagValidationLoading] = useState(false);
  const [tagForm, setTagForm] = useState(EMPTY_TAG_FORM);
  const [formValidationError, setFormValidationError] = useState("");

  const buildLogsQuery = ({ page, search, status }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(LOGS_PAGE_SIZE),
    });
    if (search) params.set("tag_number", search);
    if (status) params.set("status", status);
    return params.toString();
  };

  const fetchLogs = async ({
    page = 1,
    search = logsSearch,
    status = logsStatusFilter,
  } = {}) => {
    try {
      setLogsLoading(true);
      const { data } = await axiosInstance.get(
        `/api/fetchtagupdatelogs?${buildLogsQuery({ page, search, status })}`
      );
      setLogs(mapLogRows(data?.logs, page));
      const total =
        typeof data?.pagination?.total === "number"
          ? data.pagination.total
          : (data?.logs || []).length;
      setLogsTotal(total);
      setLogsPage(data?.pagination?.page ?? page);
    } catch (error) {
      alert(
        error?.response?.data?.message || "Unable to fetch tag update logs"
      );
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchPolicyLogs = async ({
    page = 1,
    search = policyLogsSearch,
    status = policyLogsStatusFilter,
  } = {}) => {
    try {
      setPolicyLogsLoading(true);
      const { data } = await axiosInstance.get(
        `/api/bpp/tagupdate/logs?${buildLogsQuery({ page, search, status })}`
      );
      setPolicyLogs(mapLogRows(data?.logs, page));
      const total =
        typeof data?.pagination?.total === "number"
          ? data.pagination.total
          : (data?.logs || []).length;
      setPolicyLogsTotal(total);
      setPolicyLogsPage(data?.pagination?.page ?? page);
    } catch (error) {
      alert(
        error?.response?.data?.message || "Unable to fetch policy update logs"
      );
    } finally {
      setPolicyLogsLoading(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const { data } = await axiosInstance.get("/api/getallcategories");
      setAllCategories(data?.allcategories || []);
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to fetch product categories");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLogsSearch(logsSearchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [logsSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPolicyLogsSearch(policyLogsSearchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [policyLogsSearchInput]);

  useEffect(() => {
    fetchLogs({ page: 1, search: logsSearch, status: logsStatusFilter });
  }, [logsSearch, logsStatusFilter]);

  useEffect(() => {
    fetchPolicyLogs({
      page: 1,
      search: policyLogsSearch,
      status: policyLogsStatusFilter,
    });
  }, [policyLogsSearch, policyLogsStatusFilter]);

  const handleLogsStatusFilterChange = (status) => {
    setLogsStatusFilter(status);
  };

  const clearLogsFilters = () => {
    setLogsSearchInput("");
    setLogsSearch("");
    setLogsStatusFilter("");
  };

  const handlePolicyLogsStatusFilterChange = (status) => {
    setPolicyLogsStatusFilter(status);
  };

  const clearPolicyLogsFilters = () => {
    setPolicyLogsSearchInput("");
    setPolicyLogsSearch("");
    setPolicyLogsStatusFilter("");
  };

  const resetUpdateModal = () => {
    setUpdateModalOpen(false);
    setUpdateType("product_category");
    setProductCategory("");
    setPendingTags([]);
    setTotalScan(0);
    setScanValidationError("");
    setQrError(false);
    setScannerOpen(false);
    setSampleTagInputOpen(false);
    setSampleTagValue("");
    setInsuranceValue("");
    setTagValidationOpen(false);
    setTagValidationResult(null);
    setTagValidationLoading(false);
    setTagForm(EMPTY_TAG_FORM);
    setFormValidationError("");
  };

  const handleUpdateTypeChange = (e) => {
    setUpdateType(e.target.value);
    setPendingTags([]);
    setTotalScan(0);
    setProductCategory("");
    setInsuranceValue("");
    setScanValidationError("");
    setQrError(false);
    setScannerOpen(false);
    setTagValidationOpen(false);
    setTagValidationResult(null);
    setTagValidationLoading(false);
    setTagForm(EMPTY_TAG_FORM);
    setFormValidationError("");
  };

  const openUpdateModal = () => {
    fetchAllCategories();
    setUpdateModalOpen(true);
  };

  const handleCloseScanner = () => {
    setScannerOpen(false);
    setQrError(false);
    setScanValidationError("");
    setSampleTagInputOpen(false);
    setSampleTagValue("");
    setTagValidationOpen(false);
    setTagValidationResult(null);
    setTagValidationLoading(false);
    setTagForm(EMPTY_TAG_FORM);
    setFormValidationError("");
  };

  const closeTagValidationModal = () => {
    setTagValidationOpen(false);
    setTagValidationResult(null);
    setTagValidationLoading(false);
    setTagForm(EMPTY_TAG_FORM);
    setFormValidationError("");
  };

  const buildFormFromValidationData = (data, tagNumber, requestedCategory) => {
    const tag = data?.tag || {};
    const today = toDateInputValue(new Date());
    const rawValidityStart = toDateInputValue(
      data?.validity_start || tag.validity_start
    );
    // Keep API start if today/future; otherwise blank (user picks a future date).
    const validityStart =
      rawValidityStart && rawValidityStart >= today ? rawValidityStart : "";
    const validityNumber =
      data?.validity_from_number ?? tag.validity_from_number ?? "";
    const validityUnit =
      data?.validity_from_unit || tag.validity_from_unit || "";

    // If API sends expiry_date → show it. If null/undefined → leave blank
    // (unless number + unit are present, then calculate).
    const apiExpiryRaw = data?.expiry_date ?? tag.expiry_date;
    const hasApiExpiry =
      apiExpiryRaw !== null &&
      apiExpiryRaw !== undefined &&
      apiExpiryRaw !== "";
    const expiryDate = hasApiExpiry
      ? toDateInputValue(apiExpiryRaw)
      : computeExpiryDate(validityNumber, validityUnit);

    return {
      tag_number: data?.tag_number || tag.tag_number || tagNumber || "",
      product_category:
        requestedCategory ||
        data?.requested_product_category ||
        data?.product_category ||
        tag.product_category ||
        "",
      company_name: data?.company_name || tag.company_name || "",
      validity_from_number:
        validityNumber === null || validityNumber === undefined
          ? ""
          : String(validityNumber),
      validity_from_unit: validityUnit,
      validity_start: validityStart,
      expiry_date: expiryDate,
      isDeleted: Boolean(data?.isDeleted ?? tag.isDeleted),
    };
  };

  const updateValidityNumber = (value) => {
    setTagForm((prev) => ({
      ...prev,
      validity_from_number: value,
      expiry_date: computeExpiryDate(value, prev.validity_from_unit),
    }));
    setFormValidationError("");
  };

  const updateValidityUnit = (value) => {
    setTagForm((prev) => ({
      ...prev,
      validity_from_unit: value,
      expiry_date: computeExpiryDate(prev.validity_from_number, value),
    }));
    setFormValidationError("");
  };

  const updateValidityStart = (value) => {
    const today = toDateInputValue(new Date());
    // Block past dates only when user picks one; do not auto-fill current date.
    const nextStart = value && value < today ? "" : value;
    setTagForm((prev) => ({
      ...prev,
      validity_start: nextStart,
      // Expiry is independent of validity from date.
    }));
    setFormValidationError("");
  };

  const normalizeScanValue = (value = "") => value?.trim()?.toUpperCase();

  const isDuplicateTag = (tagNumber) => {
    const normalized = normalizeScanValue(tagNumber);
    return pendingTags.some(
      (item) => normalizeScanValue(item.tag_number) === normalized
    );
  };

  const parseTagNumber = (result) => {
    if (
      result.includes("https://dashboard.tag8.in/scan/") ||
      result.includes("https://dashboard.tag8.co.in/scan/") ||
      result.includes("https://dashboard.tag8.in/#/scan/")
    ) {
      if (result.includes("https://dashboard.tag8.co.in/scan/")) {
        return result.replace("https://dashboard.tag8.co.in/scan/", "");
      }
      if (result.includes("https://dashboard.tag8.in/#/scan/")) {
        return result.replace("https://dashboard.tag8.in/#/scan/", "");
      }
      return result.replace("https://dashboard.tag8.in/scan/", "");
    }
    return null;
  };

  const validateAndAddTag = async (tagNumber) => {
    if (!productCategory) {
      setScanValidationError("Please select a product category before scanning.");
      return;
    }

    if (pendingTags.length >= MAX_TAGS) {
      setScanValidationError(`Maximum ${MAX_TAGS} tags can be scanned.`);
      return;
    }

    if (isDuplicateTag(tagNumber)) {
      setScanValidationError(
        "Tag number already scanned. Remove it from the table to scan again."
      );
      return;
    }

    try {
      setTagValidationLoading(true);
      setScanValidationError("");
      setFormValidationError("");
      const { data } = await axiosInstance.post("/api/validatetagupdaterequest", {
        tag_number: tagNumber,
        product_category: productCategory,
      });

      setTagValidationResult({
        status: Boolean(data?.status),
        message: data?.message || "",
        tag_number: data?.tag_number || tagNumber,
        product_category: data?.product_category || "",
        tag_status: data?.tag_status || "",
        requested_product_category:
          data?.requested_product_category || productCategory,
        previous_product_category: data?.previous_product_category || "",
        isDeleted: Boolean(data?.isDeleted),
        tag: data?.tag || null,
      });
      setTagForm(buildFormFromValidationData(data, tagNumber, productCategory));
      setTagValidationOpen(true);
      setScannerOpen(false);
    } catch (error) {
      const errData = error?.response?.data || {};
      const message =
        errData.message || "Unable to validate tag update request";
      setScanValidationError(message);
      setTagValidationResult({
        status: false,
        message,
        tag_number: errData.tag_number || tagNumber,
        product_category: errData.product_category || "",
        tag_status: errData.tag_status || "",
        requested_product_category: productCategory,
        isDeleted: Boolean(errData.isDeleted),
        tag: errData.tag || null,
      });
      setTagForm(
        buildFormFromValidationData(errData, tagNumber, productCategory)
      );
      setTagValidationOpen(true);
      setScannerOpen(false);
    } finally {
      setTagValidationLoading(false);
    }
  };

  const handleAcceptValidatedTag = () => {
    if (!tagValidationResult?.status) {
      closeTagValidationModal();
      return;
    }

    if (tagForm.isDeleted) {
      setFormValidationError(
        "Tag is in deleted state. Select “No (clear deleted)” to set isDeleted to false before accepting."
      );
      return;
    }

    if (pendingTags.length >= MAX_TAGS) {
      setFormValidationError(`Maximum ${MAX_TAGS} tags can be scanned.`);
      return;
    }

    if (isDuplicateTag(tagForm.tag_number || tagValidationResult.tag_number)) {
      setFormValidationError(
        "Tag number already scanned. Remove it from the table to scan again."
      );
      return;
    }

    setTotalScan((prev) => prev + 1);
    setPendingTags((prev) => [
      ...prev,
      {
        id: uuid().slice(0, 8),
        tag_number: tagForm.tag_number || tagValidationResult.tag_number,
        product_category:
          tagForm.product_category ||
          tagValidationResult.requested_product_category ||
          productCategory,
        current_product_category:
          tagValidationResult.previous_product_category ||
          tagValidationResult.product_category ||
          "",
        tag_status: tagValidationResult.tag_status || "",
        company_name: tagForm.company_name || "",
        validity_from_number: tagForm.validity_from_number,
        validity_from_unit: tagForm.validity_from_unit,
        validity_start: tagForm.validity_start,
        expiry_date: tagForm.expiry_date,
        isDeleted: false,
        clear_is_deleted: true,
        status: "Valid",
        message: tagValidationResult.message || "",
      },
    ]);
    closeTagValidationModal();
    setScannerOpen(true);
  };

  const handleRejectValidatedTag = () => {
    closeTagValidationModal();
    setScannerOpen(true);
  };

  const validateAndAddPolicyTag = async (tagNumber) => {
    if (!insuranceValue) {
      setScanValidationError("Please select an insurance value before scanning.");
      return;
    }

    if (pendingTags.length >= MAX_TAGS) {
      setScanValidationError(`Maximum ${MAX_TAGS} tags can be scanned.`);
      return;
    }

    if (isDuplicateTag(tagNumber)) {
      setScanValidationError(
        "Tag number already scanned. Remove it from the table to scan again."
      );
      return;
    }

    try {
      const { data } = await axiosInstance.post("/api/bppupdatevalidation", {
        tag_number: tagNumber,
      });

      setScanValidationError("");
      setTotalScan((prev) => prev + 1);
      setPendingTags((prev) => [
        ...prev,
        {
          id: uuid().slice(0, 8),
          tag_number: tagNumber,
          insurance_value: Number(insuranceValue),
          status: data?.status ? "Valid" : "Invalid",
          message: data?.message || "",
        },
      ]);

      if (data?.status) {
        setScannerOpen(true);
      } else {
        setScannerOpen(false);
      }
    } catch (error) {
      setScanValidationError(
        error?.response?.data?.message || "Unable to validate policy update request"
      );
      setScannerOpen(false);
    }
  };

  const handleScannedTag = async (result) => {
    const tagNumber = parseTagNumber(result);
    if (!tagNumber) {
      setQrError(true);
      return;
    }

    setQrError(false);

    if (updateType === "policy") {
      await validateAndAddPolicyTag(tagNumber);
      return;
    }

    await validateAndAddTag(tagNumber);
  };

  const handleSampleTag = (value = "") => {
    const result = value?.trim();
    if (!result) {
      return;
    }
    setQrError(false);

    if (updateType === "policy") {
      validateAndAddPolicyTag(result);
      setSampleTagInputOpen(false);
      setSampleTagValue("");
      return;
    }

    validateAndAddTag(result);
    setSampleTagInputOpen(false);
    setSampleTagValue("");
  };

  const handlePolicySubmit = async () => {
    const hasInvalidRequests = pendingTags.some(
      (item) => item.status !== "Valid"
    );

    if (hasInvalidRequests) {
      setScanValidationError(
        "Remove the invalid tags from the table before submitting your policy update."
      );
      return;
    }

    const validRequests = pendingTags.filter((item) => item.status === "Valid");

    if (!validRequests.length) {
      alert("Add at least one valid tag before submitting.");
      return;
    }

    if (!insuranceValue) {
      alert("Please select an insurance value.");
      return;
    }

    try {
      setLoader(true);
      const { data } = await axiosInstance.post("/api/bpp/tagupdate", {
        requests: validRequests.map((item) => ({
          tag_number: item.tag_number,
          insurance_value: Number(item.insurance_value || insuranceValue),
        })),
      });

      alert(data?.message || "Policy updated successfully.");
      resetUpdateModal();
      setHistoryTab(HISTORY_TABS.POLICY);
      fetchPolicyLogs({ page: 1 });
    } catch (error) {
      alert(error?.response?.data?.message || "Unable to update policy");
      resetUpdateModal();
      setHistoryTab(HISTORY_TABS.POLICY);
      fetchPolicyLogs({ page: 1 });
    } finally {
      setLoader(false);
    }
  };

  const handleSampleTagSubmit = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSampleTag(sampleTagValue);
    }
  };

  const removePendingTag = (id) => {
    setPendingTags((prev) => prev.filter((item) => item.id !== id));
    setTotalScan((prev) => Math.max(prev - 1, 0));
    setScanValidationError("");
  };

  const handleSubmitRequests = async () => {
    const hasInvalidRequests = pendingTags.some(
      (item) => item.status !== "Valid"
    );

    if (hasInvalidRequests) {
      setScanValidationError(
        "Remove the invalid tags from the table before submitting your update request."
      );
      return;
    }

    const validRequests = pendingTags.filter((item) => item.status === "Valid");

    if (!validRequests.length) {
      alert("Add at least one valid tag before submitting.");
      return;
    }

    try {
      setLoader(true);
      const { data } = await axiosInstance.post("/api/tagupdaterequests", {
        requests: validRequests.map((item) => ({
          tag_number: item.tag_number,
          product_category: item.product_category,
          company_name: item.company_name || "",
          validity_from_number:
            item.validity_from_number === "" ||
            item.validity_from_number === undefined
              ? null
              : Number(item.validity_from_number),
          validity_from_unit: item.validity_from_unit || "",
          validity_start: item.validity_start || null,
          expiry_date: item.expiry_date || null,
          isDeleted: false,
          clear_is_deleted: true,
        })),
      });
      resetUpdateModal();
      setHistoryTab(HISTORY_TABS.PRODUCT_CATEGORY);
      fetchLogs({ page: 1 });
    } catch (error) {
      alert(
        error?.response?.data?.message || "Unable to submit tag update requests"
      );
    } finally {
      setLoader(false);
    }
  };

  const handleLogsTableChange = (type, { page }) => {
    if (type === "pagination" && page !== logsPage) {
      fetchLogs({ page });
    }
  };

  const handlePolicyLogsTableChange = (type, { page }) => {
    if (type === "pagination" && page !== policyLogsPage) {
      fetchPolicyLogs({ page });
    }
  };

  const logsPageOptions = {
    page: logsPage,
    sizePerPage: LOGS_PAGE_SIZE,
    totalSize: logsTotal || logs.length,
    custom: true,
    hidePageListOnlyOnePage: false,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: true,
    showTotal: true,
    paginationTotalRenderer: (from, to, size) => (
      <span className="react-bootstrap-table-pagination-total">
        Showing {from} to {to} of {size} entries
      </span>
    ),
  };

  const policyLogsPageOptions = {
    page: policyLogsPage,
    sizePerPage: LOGS_PAGE_SIZE,
    totalSize: policyLogsTotal || policyLogs.length,
    custom: true,
    hidePageListOnlyOnePage: false,
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: true,
    showTotal: true,
    paginationTotalRenderer: (from, to, size) => (
      <span className="react-bootstrap-table-pagination-total">
        Showing {from} to {to} of {size} entries
      </span>
    ),
  };

  const logColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "previous_product_category",
      text: "Current Product Category",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "product_category",
      text: "Product Category to update",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "validity_from_number",
      text: "Validity No.",
      formatter: (cell) => renderTableCell(cell ?? "—"),
    },
    {
      dataField: "validity_from_unit",
      text: "Validity Unit",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "validity_start",
      text: "Validity From",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleDateString() : "—"),
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleDateString() : "—"),
    },
    {
      dataField: "modified_by",
      text: "Modified By",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "modified_date",
      text: "Modified Date",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleString() : ""),
    },
    {
      dataField: "admin_approved",
      text: "Admin Status",
      formatter: (cell) =>
        renderColoredCell(cell, getAdminStatusMessageColor(cell)),
    },
  ];

  const policyLogColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "insurance_value",
      text: "Insurance Value",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "product_category",
      text: "Product Category",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "validity_from_number",
      text: "Validity No.",
      formatter: (cell) => renderTableCell(cell ?? "—"),
    },
    {
      dataField: "validity_from_unit",
      text: "Validity Unit",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleDateString() : "—"),
    },
    {
      dataField: "modified_by",
      text: "Modified By",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "modified_date",
      text: "Modified Date",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleString() : ""),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) =>
        renderColoredCell(formatPolicyStatus(cell), getPolicyStatusColor(cell)),
    },
  ];

  const pendingColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "current_product_category",
      text: "Current Category",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "product_category",
      text: "New Category",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "company_name",
      text: "Company Name",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "validity_from_number",
      text: "Validity No.",
      formatter: (cell) => renderTableCell(cell ?? "—"),
    },
    {
      dataField: "validity_from_unit",
      text: "Validity Unit",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "validity_start",
      text: "Validity From",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleDateString() : "—"),
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      formatter: (cell) =>
        renderTableCell(cell ? new Date(cell).toLocaleDateString() : "—"),
    },
    {
      dataField: "tag_status",
      text: "Tag Status",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <IconButton
          aria-label="delete"
          onClick={() => removePendingTag(row.id)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const policyPendingColumns = [
    {
      dataField: "tag_number",
      text: "Tag Number",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "insurance_value",
      text: "Insurance Value",
      formatter: (cell) => renderTableCell(cell),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) =>
        renderColoredCell(cell, cell === "Valid" ? "green" : "red"),
    },
    {
      dataField: "message",
      text: "Message",
      formatter: (cell) => renderTableCell(cell || "—"),
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <IconButton
          aria-label="delete"
          onClick={() => removePendingTag(row.id)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const hasInvalidPendingTags = pendingTags.some(
    (item) => item.status !== "Valid"
  );

  const renderTableLoader = () => (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2,
        minHeight: 140,
      }}
    >
      <div style={{ fontWeight: 600, color: "#555" }}>Loading...</div>
    </div>
  );

  const renderHistoryFilters = ({
    searchValue,
    onSearchChange,
    statusValue,
    onStatusChange,
    statusOptions,
    onClear,
  }) => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        mb: 2,
      }}
    >
      <TextField
        size="small"
        label="Search Tag Number"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        sx={{ m: "0 !important", width: { xs: "100%", sm: "220px" } }}
      />
      <TextField
        select
        size="small"
        label="Status"
        value={statusValue}
        onChange={(e) => onStatusChange(e.target.value)}
        sx={{ m: "0 !important", width: { xs: "100%", sm: "180px" } }}
      >
        <MenuItem value="">All</MenuItem>
        {statusOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Button type="button" variant="outlined" onClick={onClear}>
        Clear
      </Button>
    </Box>
  );

  const renderProductCategoryHistory = () => (
    <>
      {renderHistoryFilters({
        searchValue: logsSearchInput,
        onSearchChange: setLogsSearchInput,
        statusValue: logsStatusFilter,
        onStatusChange: handleLogsStatusFilterChange,
        statusOptions: [
          { value: "pending", label: "Pending" },
          { value: "accepted", label: "Accepted" },
          { value: "rejected", label: "Rejected" },
        ],
        onClear: clearLogsFilters,
      })}
      <PaginationProvider
        pagination={paginationFactory(logsPageOptions)}
        keyField="id"
        columns={logColumns}
        data={logs}
      >
        {({ paginationProps, paginationTableProps }) => (
          <>
            <div
              className="table-responsive"
              style={{ position: "relative", minHeight: 140 }}
            >
              {logsLoading ? renderTableLoader() : null}
              <BootstrapTable
                keyField="id"
                data={logs}
                columns={logColumns}
                remote={{ pagination: true, filter: false, sort: false }}
                onTableChange={handleLogsTableChange}
                noDataIndication={() =>
                  logsLoading ? "" : "No product category update logs found"
                }
                {...paginationTableProps}
              />
            </div>
            <Row className="align-items-center mt-3 mb-2">
              <Col md="6" sm="6" className="react-bootstrap-table-pagination-total">
                <PaginationTotalStandalone {...paginationProps} />
              </Col>
              <Col
                md="6"
                sm="6"
                className="pagination pagination-rounded justify-content-end"
              >
                <PaginationListStandalone {...paginationProps} />
              </Col>
            </Row>
          </>
        )}
      </PaginationProvider>
    </>
  );

  const renderPolicyHistory = () => (
    <>
      {renderHistoryFilters({
        searchValue: policyLogsSearchInput,
        onSearchChange: setPolicyLogsSearchInput,
        statusValue: policyLogsStatusFilter,
        onStatusChange: handlePolicyLogsStatusFilterChange,
        statusOptions: [
          { value: "success", label: "Success" },
          { value: "failed", label: "Failed" },
        ],
        onClear: clearPolicyLogsFilters,
      })}
      <PaginationProvider
        pagination={paginationFactory(policyLogsPageOptions)}
        keyField="id"
        columns={policyLogColumns}
        data={policyLogs}
      >
        {({ paginationProps, paginationTableProps }) => (
          <>
            <div
              className="table-responsive"
              style={{ position: "relative", minHeight: 140 }}
            >
              {policyLogsLoading ? renderTableLoader() : null}
              <BootstrapTable
                keyField="id"
                data={policyLogs}
                columns={policyLogColumns}
                remote={{ pagination: true, filter: false, sort: false }}
                onTableChange={handlePolicyLogsTableChange}
                noDataIndication={() =>
                  policyLogsLoading ? "" : "No policy update logs found"
                }
                {...paginationTableProps}
              />
            </div>
            <Row className="align-items-center mt-3 mb-2">
              <Col md="6" sm="6" className="react-bootstrap-table-pagination-total">
                <PaginationTotalStandalone {...paginationProps} />
              </Col>
              <Col
                md="6"
                sm="6"
                className="pagination pagination-rounded justify-content-end"
              >
                <PaginationListStandalone {...paginationProps} />
              </Col>
            </Row>
          </>
        )}
      </PaginationProvider>
    </>
  );

  return (
    <div className="page-content">
      {loader ? (
        <Loader />
      ) : (
        <Box
          sx={{
            "& .MuiTextField-root": { m: 1, width: "25ch" },
          }}
          noValidate
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
        >
          <div
            style={{
              marginBottom: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <h1>Tag Update</h1>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Button
              onClick={openUpdateModal}
              style={{ margin: "1rem" }}
              variant="contained"
            >
              Update Tag
            </Button>
          </div>

          <Paper
            elevation={3}
            sx={{
              mt: 4,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "#f8f9fa",
              }}
            >
              <Tabs
                value={historyTab}
                onChange={(_, newValue) => setHistoryTab(newValue)}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    minHeight: 56,
                    color: "text.secondary",
                  },
                  "& .Mui-selected": {
                    color: "primary.main",
                  },
                }}
              >
                <Tab label="Product Category Update History" />
                <Tab label="Policy Update History" />
              </Tabs>
            </Box>

            <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: "white", minHeight: 280 }}>
              {historyTab === HISTORY_TABS.PRODUCT_CATEGORY
                ? renderProductCategoryHistory()
                : renderPolicyHistory()}
            </Box>
          </Paper>

          <Dialog fullScreen open={updateModalOpen}>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={resetUpdateModal}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  Tag Update
                </Typography>
                <Button autoFocus color="inherit" onClick={resetUpdateModal}>
                  Close
                </Button>
              </Toolbar>
            </AppBar>
            <DialogContent dividers>
              <FormControl component="fieldset" sx={{ m: 2 }}>
                <FormLabel component="legend">Choose Purpose</FormLabel>
                <RadioGroup
                  row
                  value={updateType}
                  onChange={handleUpdateTypeChange}
                >
                  <FormControlLabel
                    value="product_category"
                    control={<Radio />}
                    label="Product Category Update"
                  />
                  <FormControlLabel
                    value="policy"
                    control={<Radio />}
                    label="Add Policy To Qr Bag "
                  />
                </RadioGroup>
              </FormControl>

              {updateType === "product_category" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <div style={{ width: "50%" }}>

                  <TextField
                  fullWidth
                    select
                    label="Choose Product Category to update"
                    variant="outlined"
                    value={productCategory}
                    onChange={(e) => setProductCategory(e.target.value)}
                  >
                    {allCategories.map((category) => (
                      <MenuItem
                        key={category?._id}
                        value={category?.product_category}
                      >
                        {category?.product_category === "Dolphin Bag"
                          ? "Dolphin Bag Tracker"
                          : category?.product_category}
                      </MenuItem>
                    ))}
                  </TextField>

                  </div>
            

                  <p style={{ fontWeight: "600" }}>
                    Total no of scan : {totalScan} / {MAX_TAGS}
                  </p>

                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!productCategory) {
                        alert("Please select a product category first.");
                        return;
                      }
                      setScannerOpen(true);
                    }}
                    disabled={!productCategory || pendingTags.length >= MAX_TAGS}
                  >
                    Scan Tag
                  </Button>

                  {
                    pendingTags.length  ? <>
                  <div
                    style={{ backgroundColor: "white", width: "100%" }}
                    className="table-responsive"
                  >
                    <BootstrapTable
                      keyField="id"
                      data={pendingTags}
                      columns={pendingColumns}
                    />
                  </div>

                  {hasInvalidPendingTags && (
                    <div
                      role="alert"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        color: "#b71c1c",
                        backgroundColor: "#ffebee",
                        border: "1px solid #ef9a9a",
                        borderRadius: "4px",
                      }}
                    >
                      Some scanned tags could not be validated. Remove the
                      invalid entries from the table before submitting your
                      update request.
                    </div>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitRequests}
                    disabled={
                      !pendingTags.some((item) => item.status === "Valid") ||
                      hasInvalidPendingTags
                    }
                  >
                    Submit
                  </Button>
                    </> : null
                  }

        
                </Box>
              )}

              {updateType === "policy" && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <div style={{ width: "50%" }}>
                    <TextField
                      fullWidth
                      select
                      label="Choose Insurance Value"
                      variant="outlined"
                      value={insuranceValue}
                      onChange={(e) => {
                        setInsuranceValue(e.target.value);
                      }}
                    >
                      <MenuItem value={3000}>3000</MenuItem>
                      <MenuItem value={5000}>5000</MenuItem>
                    </TextField>
                  </div>

                  <p style={{ fontWeight: "600" }}>
                    Total no of scan : {totalScan} / {MAX_TAGS}
                  </p>

                  <Button
                    variant="contained"
                    onClick={() => {
                      if (!insuranceValue) {
                        alert("Please select an insurance value first.");
                        return;
                      }
                      setScanValidationError("");
                      setQrError(false);
                      setScannerOpen(true);
                    }}
                    disabled={!insuranceValue || pendingTags.length >= MAX_TAGS}
                  >
                    Start Scan
                  </Button>

                  {pendingTags.length ? (
                    <>
                      <div
                        style={{ backgroundColor: "white", width: "100%" }}
                        className="table-responsive"
                      >
                        <BootstrapTable
                          keyField="id"
                          data={pendingTags}
                          columns={policyPendingColumns}
                        />
                      </div>

                      {hasInvalidPendingTags && (
                        <div
                          role="alert"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            color: "#b71c1c",
                            backgroundColor: "#ffebee",
                            border: "1px solid #ef9a9a",
                            borderRadius: "4px",
                          }}
                        >
                          Some scanned tags could not be validated. Remove the
                          invalid entries from the table before submitting your
                          policy update.
                        </div>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePolicySubmit}
                        disabled={
                          !pendingTags.some((item) => item.status === "Valid") ||
                          hasInvalidPendingTags
                        }
                      >
                        Submit
                      </Button>
                    </>
                  ) : null}
                </Box>
              )}
            </DialogContent>
          </Dialog>

          <Dialog fullWidth maxWidth="xs" open={scannerOpen}>
            <DialogTitle style={{ textAlign: "center", fontWeight: "bold" }}>
              Scan TagNumber
            </DialogTitle>
            <p style={{ textAlign: "center", fontWeight: "500" }}>
              Please scan your TagNumber QR
            </p>
            <small style={{ textAlign: "center", display: "block" }}>
              Using your QR Gun
            </small>
            <small style={{ textAlign: "center", display: "block" }}>
              Total no of scan : {totalScan} / {MAX_TAGS}
            </small>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarcodeReader
                onScan={handleScannedTag}
                onError={() => {}}
                style={{
                  width: "50%",
                  marginLeft: "4rem",
                  marginBottom: "2rem",
                }}
              />
            </div>

            {tagValidationLoading ? (
              <p style={{ textAlign: "center", marginTop: "1rem" }}>
                Validating tag...
              </p>
            ) : null}

            {qrError ? (
              <p
                style={{
                  textAlign: "center",
                  color: "red",
                  marginTop: "2rem",
                }}
              >
                Invalid QR
              </p>
            ) : null}
            {scanValidationError ? (
              <p
                style={{
                  textAlign: "center",
                  color: "red",
                  marginTop: "1rem",
                  padding: "0 1rem",
                }}
              >
                {scanValidationError}
              </p>
            ) : null}
            <DialogActions>
              <Button onClick={handleCloseScanner}>Close</Button>
              {local && (
                <>
                  {sampleTagInputOpen ? (
                    <TextField
                      autoFocus
                      size="small"
                      label="Sample Tag"
                      value={sampleTagValue}
                      onChange={(e) => setSampleTagValue(e.target.value)}
                      onKeyDown={handleSampleTagSubmit}
                    />
                  ) : null}
                  <Button
                    onClick={() => {
                      setSampleTagInputOpen((prev) => !prev);
                      setSampleTagValue("");
                    }}
                  >
                    sample
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={tagValidationOpen}
            onClose={
              tagValidationResult?.status
                ? handleRejectValidatedTag
                : () => {
                    closeTagValidationModal();
                    setScannerOpen(true);
                  }
            }
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                pb: 1,
                borderBottom: "1px solid #e7e7e7",
              }}
            >
              Tag Validation Form
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              {tagValidationResult ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 1,
                      backgroundColor: tagValidationResult.status
                        ? "#e8f5e9"
                        : "#ffebee",
                      color: tagValidationResult.status ? "#1b5e20" : "#b71c1c",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    {tagValidationResult.message ||
                      (tagValidationResult.status
                        ? "Tag is available for update"
                        : "Tag is not available for update")}
                  </Box>

                  <TextField
                    label="Tag Number"
                    value={tagForm.tag_number || "—"}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText="Fixed from scanned tag"
                  />

                  <TextField
                    label="Product Category"
                    value={
                      tagForm.product_category === "Dolphin Bag"
                        ? "Dolphin Bag Tracker"
                        : tagForm.product_category || "—"
                    }
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText="Fixed from selected update category"
                  />

                  <TextField
                    label="Company Name"
                    value={tagForm.company_name}
                    onChange={(e) => {
                      setTagForm((prev) => ({
                        ...prev,
                        company_name: e.target.value,
                      }));
                      setFormValidationError("");
                    }}
                    fullWidth
                    disabled={!tagValidationResult.status}
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Validity Number"
                      type="number"
                      value={tagForm.validity_from_number}
                      onChange={(e) => updateValidityNumber(e.target.value)}
                      fullWidth
                      inputProps={{ min: 0 }}
                      disabled={!tagValidationResult.status}
                    />
                    <TextField
                      select
                      label="Validity Unit"
                      value={tagForm.validity_from_unit}
                      onChange={(e) => updateValidityUnit(e.target.value)}
                      fullWidth
                      disabled={!tagValidationResult.status}
                    >
                      <MenuItem value="">Select unit</MenuItem>
                      {VALIDITY_UNITS.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Validity From Date"
                      type="date"
                      value={tagForm.validity_start}
                      onChange={(e) => updateValidityStart(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: toDateInputValue(new Date()) }}
                      disabled={!tagValidationResult.status}
                    />
                    <TextField
                      label="Expiry Date"
                      type="date"
                      value={tagForm.expiry_date}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                      helperText={
                        tagForm.expiry_date
                          ? "recalculated from validity number and unit"
                          : "calculated from number & unit"
                      }
                      disabled={!tagValidationResult.status}
                    />
                  </Box>

                  {tagValidationResult.isDeleted ? (
                    <FormControl
                      component="fieldset"
                      disabled={!tagValidationResult.status}
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        borderRadius: 1,
                        border: tagForm.isDeleted
                          ? "1px solid #ef9a9a"
                          : "1px solid #e0e0e0",
                        backgroundColor: tagForm.isDeleted
                          ? "#fff8f8"
                          : "#fafafa",
                      }}
                    >
                   
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", mb: 1 }}
                      >
                        Tag is currently deleted. You must clear this before
                        accepting.
                      </Typography>
                      <RadioGroup
                        row
                        value={tagForm.isDeleted ? "true" : "false"}
                        onChange={(e) => {
                          setTagForm((prev) => ({
                            ...prev,
                            isDeleted: e.target.value === "true",
                          }));
                          setFormValidationError("");
                        }}
                      >
                        <FormControlLabel
                          value="false"
                          control={<Radio color="success" />}
                          label="Yes"
                        />
                         <FormControlLabel
                          value="true"
                          control={<Radio color="error" />}
                          label="No"
                        />
                      </RadioGroup>
                    </FormControl>
                  ) : null}

                  {formValidationError ? (
                    <Typography sx={{ color: "#b71c1c", fontWeight: 500 }}>
                      {formValidationError}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              {tagValidationResult?.status ? (
                <>
                  <Button color="inherit" onClick={handleRejectValidatedTag}>
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptValidatedTag}
                    disabled={tagForm.isDeleted}
                  >
                    Accept
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    closeTagValidationModal();
                    setScannerOpen(true);
                  }}
                >
                  Close
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </div>
  );
}

export default TagUpdate;
